import React, { useState, useEffect } from "react";
import axios from "../common/axios";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../components/ui/card";
import ShadcnPageWrapper from "./ShadcnPageWrapper";

export default function SignUp() {
  const navigate = useNavigate(); // useNavigate 사용

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [disciplines, setDisciplines] = useState([]);
  const [passwordValid, setPasswordValid] = useState([false, false, false, false]);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [openTermsDialog, setOpenTermsDialog] = useState(false);
  const [showCompanyInput, setShowCompanyInput] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [strength, setStrength] = useState(0);

  function getPasswordStrength(password) {
    let score = 0;
    const consecutivePattern = /(.)\1\1+/; // 3번 이상 연속되는 문자

    if (password.length >= 10) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (consecutivePattern.test(password)) score = 0; // 연속된 문자가 있다면 점수를 0으로 초기화

    return score;
  }

  useEffect(() => {
    setStrength(getPasswordStrength(password));
  }, [password]);

  const handleEmailChange = (e) => {
    const emailInput = e.target.value;
    setEmail(emailInput);
    setIsEmailValid(validateEmail(emailInput) || emailInput === "");
  };

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    setShowCompanyInput(email.length > 0 && !email.endsWith("@samsung.com"));
  }, [email]);

  const handleOpenTermsDialog = () => setOpenTermsDialog(true);
  const handleCloseTermsDialog = () => setOpenTermsDialog(false);

  const handleDisciplineChange = (name, checked) => {
    setDisciplines((prev) =>
      checked ? [...prev, name] : prev.filter((discipline) => discipline !== name)
    );
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    const validity = [
      value.length >= 8,
      /[A-Z]/.test(value),
      /[a-z]/.test(value),
      /[!@#$%^&*]/.test(value),
    ];
    setPasswordValid(validity);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // API call logic here
    try {
      const response = await axios.post("/dpsignup", {
        username,
        password,
        email,
        company,
        disciplines,
      });

      if (response.data.success) {
        Swal.fire(
          "성공",
          "회원 가입이 완료되었습니다. 가입 승인 메일을 받으신 후에 로그인이 가능합니다.",
          "success"
        );
        navigate("/dplogin"); // Redirect on success
      } else {
        // Handle backend validation errors
        Swal.fire("Error", response.data.message || "회원 가입 실패", "error");
      }
    } catch (error) {
      // Handle network or other unexpected errors
      Swal.fire(
        "Error",
        error.response?.data?.message || "회원 가입에 문제가 발생했습니다.",
        "error"
      );
    }
  };

  const disciplineOptions = ["설비", "건축", "전기", "제어"];

  // State to manage if the form is valid to submit
  const [isFormValid, setIsFormValid] = useState(false);

  // Check if all fields are valid and if passwords match
  useEffect(() => {
    const formIsValid =
      username &&
      password &&
      confirmPassword &&
      email &&
      isEmailValid &&
      (!showCompanyInput || (showCompanyInput && company)) &&
      disciplines.length > 0 &&
      password === confirmPassword &&
      passwordValid.every(Boolean) &&
      acceptedTerms;

    setIsFormValid(formIsValid);
  }, [
    username,
    password,
    confirmPassword,
    email,
    company,
    disciplines,
    passwordValid,
    acceptedTerms,
    isEmailValid,
    showCompanyInput,
  ]);

  return (
    <ShadcnPageWrapper>
      <Dialog open={openTermsDialog} onOpenChange={setOpenTermsDialog}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>개인정보 수집 이용 동의</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground">
            <p>
              DP시스템 이용을 위하여 본인의 개인정보를 수집·이용하고자 하는 경우에는「개인정보
              보호법」 제15조 제1항 제1호, 제22조 제3항에 따라 본인의 동의를 얻어야 합니다. 이에
              본인은 귀 사가 아래의 내용과 같이 본인의 개인정보를 수집·이용 또는 제공하는 것에
              동의합니다.
            </p>

            <h3>[수집·이용에 관한 사항]</h3>
            <ul>
              <li>
                <h4>1. 수집·이용 목적</h4>
                <p>DP 시스템 이용</p>
              </li>
              <li>
                <h4>2. 수집·이용할 항목</h4>
                <p>이메일, 회사명, 사업자등록번호</p>
              </li>
              <li>
                <h4>3. 보유·이용 기간</h4>
                <p>
                  위 개인정보는 수집·이용에 관한 동의일로부터 동의 철회시까지 보유·이용할 수
                  있습니다. 단, 동의 철회일 후에는 위에 기재된 목적과 관련된 분쟁 해결, 민원처리,
                  법령상 의무이행을 위하여 필요한 범위 내에서만 보유·이용됩니다.
                </p>
              </li>
              <li>
                <h4>4. 동의를 거부할 권리 및 동의를 거부할 경우의 불이익</h4>
                <p>
                  위 개인정보의 수집·이용에 대한 동의를 거부할 수 있으며, 동의 후에도 언제든지 철회
                  가능합니다. 다만, 동의하지 않은 수집·이용 목적과 관련된 광고심의시스템 이용 관련
                  서비스 및 안내 등의 편의는 제한될 수 있습니다.
                </p>
              </li>
            </ul>
          </div>
          <div className="flex items-center gap-1.5">
            <Checkbox
              checked={acceptedTerms}
              onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
            />
            <span className="text-sm">위 내용에 동의합니다.</span>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={handleCloseTermsDialog}>
              닫기
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex min-h-screen flex-col items-center justify-center px-4 py-12 bg-slate-100">
        <div className="w-full sm:w-[400px]">
          <div className="mb-6 flex flex-col items-center gap-2">
            <img src="/images/g1313.png" alt="삼성중공업" className="h-10 w-auto" />
            <h1 className="m-0 text-xl font-bold text-foreground">
              삼성중공업 하이테크 통합 DP 시스템
            </h1>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base no-underline">회원 가입</CardTitle>
              <CardDescription>Samsung Heavy Industries Hightech</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="username">아이디</Label>
                  <Input
                    id="username"
                    type="text"
                    name="username"
                    placeholder="로그인 아이디"
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="off"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="password">비밀번호</Label>
                  <Input
                    id="password"
                    type="password"
                    name="password"
                    placeholder="비밀번호"
                    value={password}
                    onChange={handlePasswordChange}
                    autoComplete="off"
                  />
                  <div className="flex items-center gap-2 pt-1">
                    <div
                      className={`h-1 flex-1 rounded-full ${
                        strength > 0 ? "bg-green-500" : "bg-gray-200"
                      }`}
                    ></div>
                    <div
                      className={`h-1 flex-1 rounded-full ${
                        strength > 1 ? "bg-green-500" : "bg-gray-200"
                      }`}
                    ></div>
                    <div
                      className={`h-1 flex-1 rounded-full ${
                        strength > 2 ? "bg-green-500" : "bg-gray-200"
                      }`}
                    ></div>
                    <div
                      className={`h-1 flex-1 rounded-full ${
                        strength > 3 ? "bg-green-500" : "bg-gray-200"
                      }`}
                    ></div>
                    <div
                      className={`h-1 flex-1 rounded-full ${
                        strength > 4 ? "bg-green-500" : "bg-gray-200"
                      }`}
                    ></div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    10자 이상의 대소문자, 특수문자를 포함해주세요. 3자리 이상의 연속된 문자는 쓸
                    수 없습니다.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="confirm-password">비밀번호 확인</Label>
                  <Input
                    id="confirm-password"
                    placeholder="비밀번호 확인"
                    name="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="off"
                  />
                  {password && confirmPassword && password !== confirmPassword && (
                    <p className="text-xs text-destructive">비밀번호가 일치하지 않습니다.</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email">이메일</Label>
                  <Input
                    id="email"
                    placeholder="이메일"
                    name="email"
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    autoComplete="off"
                  />
                  {!isEmailValid && email.length > 0 && (
                    <p className="text-xs text-destructive">유효한 이메일 형식이 아닙니다.</p>
                  )}
                </div>

                {showCompanyInput && (
                  <div className="space-y-1.5">
                    <Label htmlFor="company">사업자등록번호</Label>
                    <Input
                      id="company"
                      placeholder="사업자등록번호(-를 함께 써주세요)"
                      name="company"
                      type="text"
                      onChange={(e) => setCompany(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label>공종</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="flex h-9 w-full items-center justify-start rounded-md border border-input bg-transparent px-3 py-1 text-left text-base shadow-[0_1px_2px_0_rgb(0,0,0,0.05)] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:text-sm"
                      >
                        {disciplines.length > 0 ? (
                          <span className="truncate">{disciplines.join(", ")}</span>
                        ) : (
                          <span className="text-muted-foreground">공종을 선택해주세요</span>
                        )}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-2" align="start">
                      <div className="space-y-2">
                        {disciplineOptions.map((name) => (
                          <label
                            key={name}
                            className="flex items-center gap-2 text-sm cursor-pointer"
                          >
                            <Checkbox
                              checked={disciplines.indexOf(name) > -1}
                              onCheckedChange={(checked) =>
                                handleDisciplineChange(name, checked === true)
                              }
                            />
                            {name}
                          </label>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="flex items-center gap-1.5">
                  <Checkbox
                    checked={acceptedTerms}
                    onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
                  />
                  <span className="text-sm text-muted-foreground">
                    개인정보 수집에{" "}
                    <a href="#" className="text-[hsl(222,80%,40%)] hover:underline" onClick={handleOpenTermsDialog}>
                      동의
                    </a>
                    합니다.
                  </span>
                </div>

                <Button
                  type="submit"
                  disabled={!isFormValid}
                  className="w-full border-0 bg-[hsl(222,80%,40%)] text-white hover:bg-[hsl(222,80%,40%)]/80"
                >
                  회원 가입 요청
                </Button>
              </form>

              <p className="mt-4 text-center text-sm text-muted-foreground">
                이미 삼성중공업 하이테크의 DP 회원인가요?{" "}
                <RouterLink
                  to={{ pathname: "/dplogin" }}
                  className="font-medium text-[hsl(222,80%,40%)] hover:underline"
                >
                  로그인
                </RouterLink>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </ShadcnPageWrapper>
  );
}
